import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { IncidentDetail } from "../api/types";
import { formatDate, formatDateTime } from "../lib/workorders";
import {
  INCIDENT_SEVERITIES,
  INCIDENT_STATUSES,
  INCIDENT_TYPES,
  formatIncidentId,
  incidentStatusBadge,
  incidentStatusLabel,
  incidentTypeLabel,
  severityLabel,
  toDatetimeLocal,
} from "../lib/incidents";

export function IncidentDetailPage() {
  const { id } = useParams();
  const incidentId = Number(id);

  const incidentQ = useQuery({
    queryKey: ["incident", incidentId],
    queryFn: () => api.incidents.get(incidentId),
    enabled: Number.isInteger(incidentId),
  });

  if (incidentQ.isLoading) {
    return (
      <Layout title="Incident">
        <p className="sub">Loading…</p>
      </Layout>
    );
  }
  if (incidentQ.isError || !incidentQ.data) {
    return (
      <Layout title="Incident">
        <section className="card">
          <p>That incident could not be found.</p>
          <Link to="/incidents" className="btn btn-ghost btn-xl">
            Back to Incidents
          </Link>
        </section>
      </Layout>
    );
  }

  return <IncidentDetailView incident={incidentQ.data.incident} />;
}

function IncidentDetailView({ incident }: { incident: IncidentDetail }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [incidentType, setIncidentType] = useState(incident.incidentType ?? "");
  const [incidentDatetime, setIncidentDatetime] = useState(
    toDatetimeLocal(incident.incidentDatetime),
  );
  const [severity, setSeverity] = useState(incident.severity);
  const [status, setStatus] = useState(incident.status);
  const [description, setDescription] = useState(incident.description);
  const [reporterPhone, setReporterPhone] = useState(incident.reporterPhone ?? "");
  const [followUpAction, setFollowUpAction] = useState(incident.followUpAction ?? "");
  const [notes, setNotes] = useState(incident.notes ?? "");
  const [complaintNote, setComplaintNote] = useState("");
  const [saved, setSaved] = useState(false);

  const saveMut = useMutation({
    mutationFn: () =>
      api.incidents.update(incident.id, {
        incidentType: incidentType || null,
        incidentDatetime: incidentDatetime || undefined,
        severity,
        status,
        description: description.trim(),
        reporterPhone: reporterPhone.trim() || null,
        followUpAction: followUpAction.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["incident", incident.id] });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    },
  });

  const addNoteMut = useMutation({
    mutationFn: () => api.incidents.addNote(incident.id, complaintNote.trim()),
    onSuccess: () => {
      setComplaintNote("");
      queryClient.invalidateQueries({ queryKey: ["incident", incident.id] });
    },
  });

  return (
    <Layout title="Incident Details">
      <section className="card form-card">
        <div className="detail-head">
          <div>
            <h2>
              {formatIncidentId(incident.id)} — {incidentTypeLabel(incident.incidentType)}
            </h2>
            <p className="sub">
              {incident.where} · {incident.buildingName}
              {incident.tenantName ? ` · ${incident.tenantName}` : ""} · Logged{" "}
              {formatDate(incident.createdAt)}
            </p>
          </div>
          <span className={`badge badge-${incidentStatusBadge(incident.status)}`}>
            {incidentStatusLabel(incident.status)}
          </span>
        </div>

        <p className="help-line">Edit the incident and tap Save changes.</p>

        <div className="two-col">
          <label className="field">
            <span>Type</span>
            <select value={incidentType} onChange={(e) => setIncidentType(e.target.value)}>
              <option value="">Not set</option>
              {INCIDENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {incidentTypeLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Date &amp; time</span>
            <input
              type="datetime-local"
              value={incidentDatetime}
              onChange={(e) => setIncidentDatetime(e.target.value)}
            />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Severity</span>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {INCIDENT_SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {severityLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {INCIDENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {incidentStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span>Phone number</span>
          <input value={reporterPhone} onChange={(e) => setReporterPhone(e.target.value)} />
        </label>

        <label className="field">
          <span>Description</span>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Follow-up action</span>
          <textarea
            rows={3}
            value={followUpAction}
            onChange={(e) => setFollowUpAction(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Notes</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <div className="form-actions">
          {saved && !saveMut.isPending && <span className="saved-note">Saved.</span>}
          {saveMut.isError && <span className="field-error">Could not save. Try again.</span>}
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/incidents")}
            disabled={saveMut.isPending}
          >
            Back to list
          </button>
          <button
            className="btn btn-primary btn-xl"
            onClick={() => {
              setSaved(false);
              saveMut.mutate();
            }}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Complaint history</h2>
        {incident.incidentNotes.length === 0 ? (
          <p className="sub">No additional complaint notes recorded yet.</p>
        ) : (
          <ul className="timeline">
            {incident.incidentNotes.map((n) => (
              <li key={n.id}>
                <strong>{formatDateTime(n.createdAt)}</strong> - {n.authorName}: {n.note}
              </li>
            ))}
          </ul>
        )}
        <div className="note-area">
          <textarea
            rows={3}
            value={complaintNote}
            onChange={(e) => setComplaintNote(e.target.value)}
            placeholder="Add another complaint or follow-up note..."
          />
        </div>
        <div className="form-actions">
          <button
            className="btn btn-primary btn-xl"
            onClick={() => addNoteMut.mutate()}
            disabled={!complaintNote.trim() || addNoteMut.isPending}
          >
            {addNoteMut.isPending ? "Adding..." : "Add complaint note"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
